// SEQ-193: pure logic for the "Month on month" widget requested at the 24
// September meeting ("is there an option for us to compare months of a
// certain item, like the patching one, we can make our own patching one to
// compare months"). Columns are calendar months in the widget's own
// resolved date range; rows are either several metrics (company-wide) or
// one metric's projects, matching how the rest of the builder already models
// "group by".
//
// PURE STRING ARITHMETIC, NO Date/timezone. Exactly the discipline
// fiscalYear.ts documents at its own file header, and for the same reason:
// a `new Date(y, m, 1)` built from local components and then read back with
// `toISOString()` (UTC) is precisely the claim-timing defect class this repo
// already carries a fix for. `monthsInRange` below only ever slices and pads
// the ISO string's own year/month digits, so it produces the same eleven
// months for "2025-07-01".."2026-05-31" whether the process runs under
// TZ=Australia/Brisbane or TZ=UTC (monthOnMonth.test.ts proves both).
//
// EMPTY MONTHS ARE BLANK, NOT ZERO. A calendar month the widget's own range
// covers but that has no bucket at all (the company or the project did
// nothing that month) must read as "no data", not "$0", exactly like every
// other null in this feature (rollup.ts's payroll-source fields, the RPC's
// own `metricsAsAt`). `buildMonthOnMonthTable` never invents a 0: a
// `valueForMonth` that returns null for a given month leaves that cell null,
// and `monthsInRange` is what makes sure a month with literally no row still
// gets a column to be null in.

import type { PnlAnalyticsRow } from "../../lib/pnl/analyticsClient";
import type { PnlUnit } from "../../lib/pnl/pnlAnalytics";
import type { PnlWidgetFilter } from "../../lib/pnl/pnlWidgetSchema";
import { formatMonthLabel } from "./pnlAnalyticsFormat";

/** SEQ-193. The most projects a single "group by project" month-on-month widget may break out, one RPC call each (see useMonthOnMonthProjectRows.ts). Bounded and explicit, matching this feature's own "your own chosen projects" framing rather than an unbounded, dashboard-wide breakdown the RPC has no single call for. */
export const MONTH_ON_MONTH_MAX_PROJECTS = 8;

/** SEQ-193 review: company mode caps its metric rows at the same ceiling project mode caps its projects at. */
export const MONTH_ON_MONTH_MAX_METRICS = MONTH_ON_MONTH_MAX_PROJECTS;

/** Keeps the first `MONTH_ON_MONTH_MAX_METRICS` keys, so a saved widget with dozens of metrics cannot draw an unbounded table. */
export function capMonthOnMonthMetrics(keys: string[]): string[] {
  return keys.slice(0, MONTH_ON_MONTH_MAX_METRICS);
}

export interface MonthOnMonthColumn {
  /** ISO 'YYYY-MM-01'. */
  periodMonth: string;
  /** "Jul 2025", from the same formatter every other month label on this page uses. */
  label: string;
  /** True when this month is not complete (the range ends before its last day, or it is the current month), so its figures are month to date. */
  partial: boolean;
}

/**
 * What "complete" is measured against. Both are plain ISO 'YYYY-MM-DD'
 * text. `todayIso` is passed in (never read from a clock here) so the logic
 * stays pure and testable.
 */
export interface MonthOnMonthRangeContext {
  rangeToIso: string;
  todayIso: string;
}

export interface MonthOnMonthChange {
  /** last month's value minus the one before it, in the row's own unit. Null when either side is missing. */
  deltaValue: number | null;
  /**
   * `deltaValue` as a percent of the PRIOR month's magnitude
   * (`deltaValue / abs(prior)) x 100`), so a percent change against a
   * negative prior value (net profit swinging from a loss) still reads with
   * the sign of the movement itself, not flipped by a negative denominator.
   * Null when either side is missing, or the prior month was exactly zero
   * (division by zero is not "0% change").
   */
  deltaPercent: number | null;
}

export interface MonthOnMonthRow {
  /** A metric key in "company" mode, a project id in "project" mode. */
  key: string;
  label: string;
  unit: PnlUnit;
  /** One entry per column, aligned by index. Null means no data for that month, never 0. */
  values: (number | null)[];
  /** Present only when the widget asked for a change column and there are two complete months to compare. */
  change: MonthOnMonthChange | null;
  /** True when this row's data failed to load (project mode), so the row shows "Couldn't load" instead of a run of N/A. */
  loadFailed?: boolean;
}

export interface MonthOnMonthTable {
  columns: MonthOnMonthColumn[];
  rows: MonthOnMonthRow[];
  /** The Change column header: "Change, Jul to Aug" when it names the months compared, plain "Change" when there is nothing to compare. */
  changeLabel: string;
}

/** One row's worth of per-month lookup, kept a plain function so the caller decides how a month's value is found (a company-wide row map, or one project's own row map) without this module knowing about PnlAnalyticsRow shapes. */
export interface MonthOnMonthSeriesInput {
  key: string;
  label: string;
  unit: PnlUnit;
  /** The value for one calendar month ('YYYY-MM-01'), or null when there is no data for it. */
  valueForMonth: (periodMonth: string) => number | null;
  /** True when this series' data failed to load. */
  loadFailed?: boolean;
}

/**
 * Every calendar month from `fromIso` to `toIso` inclusive, ascending, as
 * 'YYYY-MM-01' strings. Both bounds are read as plain ISO text (year and
 * month digits only), never constructed into a `Date`, so this is exact in
 * every timezone including AEST (see file header).
 */
export function monthsInRange(fromIso: string, toIso: string): string[] {
  const from = parseYearMonth(fromIso, "fromIso");
  const to = parseYearMonth(toIso, "toIso");

  const months: string[] = [];
  let year = from.year;
  let month = from.month;
  let guard = 0;
  while (year < to.year || (year === to.year && month <= to.month)) {
    months.push(isoMonthStart(year, month));
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
    guard += 1;
    if (guard > 1200) {
      // A century of months is already far past any date range this page's
      // filter bar or widget editor can produce; a range this wide is almost
      // certainly a swapped from/to, and this stops it hanging the tab.
      throw new Error(`monthsInRange: "${fromIso}"..."${toIso}" spans over 100 years, refusing to enumerate it`);
    }
  }
  return months;
}

/**
 * Security review finding (SEQ-193): `monthsInRange` throws on a malformed
 * date or a range over 100 years, and `PnlMonthOnMonthTable` was calling it
 * unguarded during render. A saved dashboard with a bad custom range (a
 * typo'd year an `<input type="month">` still accepts, or a swapped
 * from/to) would take the whole page down for every viewer, with no widget
 * boundary to catch it. This never throws: a bad date, or a range wide
 * enough to need more columns than a person would ever compare side by
 * side, returns null instead, so the caller can show a message.
 */
export const MONTH_ON_MONTH_MAX_COLUMNS = 60;

export function safeMonthsInRange(fromIso: string, toIso: string): string[] | null {
  try {
    const months = monthsInRange(fromIso, toIso);
    return months.length > MONTH_ON_MONTH_MAX_COLUMNS ? null : months;
  } catch {
    return null;
  }
}

function parseYearMonth(iso: string, label: string): { year: number; month: number } {
  const match = /^(\d{4})-(\d{2})-\d{2}$/.exec(iso);
  if (!match) throw new Error(`monthsInRange: ${label} "${iso}" is not an ISO yyyy-MM-dd date`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) throw new Error(`monthsInRange: ${label} "${iso}" has an invalid month`);
  return { year, month };
}

function isoMonthStart(year: number, month: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-01`;
}

/**
 * The trailing "Change" column: the last two columns' values, last minus the
 * one before it. Requires at least two months and both values present;
 * anything else is null (blank), never a fabricated 0.
 */
export function buildMonthOnMonthChange(values: (number | null)[]): MonthOnMonthChange | null {
  if (values.length < 2) return null;
  return buildChangeBetween(values, values.length - 2, values.length - 1);
}

function buildChangeBetween(values: (number | null)[], priorIndex: number, lastIndex: number): MonthOnMonthChange | null {
  const prior = values[priorIndex];
  const last = values[lastIndex];
  if (prior === undefined || last === undefined || prior === null || last === null) return null;
  const deltaValue = last - prior;
  const deltaPercent = prior !== 0 ? (deltaValue / Math.abs(prior)) * 100 : null;
  return { deltaValue, deltaPercent };
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28;
  return [4, 6, 9, 11].includes(month) ? 30 : 31;
}

/**
 * True when the LAST month of a range is not complete: the range ends before
 * that month's last day, or that month is the current month (or later). A
 * partial month compared with a full one reads as a false drop, so the
 * Change column skips it. Pure string arithmetic, like everything here.
 */
function lastMonthIsPartial(lastMonthIso: string, ctx: MonthOnMonthRangeContext): boolean {
  let last, today, to;
  try {
    last = parseYearMonth(lastMonthIso, "lastMonthIso");
    today = parseYearMonth(ctx.todayIso, "todayIso");
    to = parseYearMonth(ctx.rangeToIso, "rangeToIso");
  } catch {
    // Never throw during render: an unreadable date means "treat as complete".
    return false;
  }
  if (last.year > today.year || (last.year === today.year && last.month >= today.month)) return true;
  const toDay = Number(ctx.rangeToIso.slice(8, 10));
  if (to.year !== last.year || to.month !== last.month) return false;
  return toDay < daysInMonth(last.year, last.month);
}

interface ChangeWindow {
  priorIndex: number;
  lastIndex: number;
  label: string;
}

function resolveChangeWindow(months: string[], partialLast: boolean): ChangeWindow | null {
  const lastIndex = partialLast ? months.length - 2 : months.length - 1;
  const priorIndex = lastIndex - 1;
  if (priorIndex < 0) return null;
  const prior = formatMonthLabel(months[priorIndex]);
  const last = formatMonthLabel(months[lastIndex]);
  const sameYear = months[priorIndex].slice(0, 4) === months[lastIndex].slice(0, 4);
  const label = sameYear ? `Change, ${prior.slice(0, 3)} to ${last.slice(0, 3)}` : `Change, ${prior} to ${last}`;
  return { priorIndex, lastIndex, label };
}

/**
 * Turns a set of months and a set of row series into the table the widget
 * renders. `showChange` is a widget-level switch (the plan's "optional
 * change column"), applied identically to every row.
 */
export function buildMonthOnMonthTable(
  months: string[],
  series: MonthOnMonthSeriesInput[],
  showChange: boolean,
  range?: MonthOnMonthRangeContext,
): MonthOnMonthTable {
  // Without a range context every month is treated as complete (the old behaviour).
  const partialLast = range !== undefined && months.length > 0 && lastMonthIsPartial(months[months.length - 1], range);
  const columns: MonthOnMonthColumn[] = months.map((periodMonth, index) => ({
    periodMonth,
    label: formatMonthLabel(periodMonth),
    partial: partialLast && index === months.length - 1,
  }));
  const window = resolveChangeWindow(months, partialLast);

  const rows: MonthOnMonthRow[] = series.map((one) => {
    const values = months.map((month) => one.valueForMonth(month));
    return {
      key: one.key,
      label: one.label,
      unit: one.unit,
      values,
      change: showChange && window ? buildChangeBetween(values, window.priorIndex, window.lastIndex) : null,
      ...(one.loadFailed ? { loadFailed: true } : {}),
    };
  });

  return { columns, rows, changeLabel: window ? window.label : "Change" };
}

/** A lookup from calendar month to that month's row, for a set of month-grain rows (one row per month, `periodMonth` set, `projectId` null or a single project's). Rows with no `periodMonth` are dropped: they are not part of this table. */
export function monthRowByPeriod(rows: PnlAnalyticsRow[]): Map<string, PnlAnalyticsRow> {
  const map = new Map<string, PnlAnalyticsRow>();
  for (const row of rows) {
    if (row.periodMonth) map.set(row.periodMonth, row);
  }
  return map;
}

/**
 * Reads a month-on-month "group by project" widget's own project shortlist
 * off its `filters` (the same `projectId` filter every other widget already
 * supports, `resolveWidgetFilters` in dashboardRequestKeys.ts). Returns []
 * when the widget names none, which the widget shows as "pick 1 to 8
 * projects" rather than silently guessing the whole company (see
 * MONTH_ON_MONTH_MAX_PROJECTS).
 */
export function projectIdsFromFilters(filters: PnlWidgetFilter[]): string[] {
  for (const filter of filters) {
    if (filter.field !== "projectId" && filter.field !== "project_id") continue;
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    return values.map(String);
  }
  return [];
}

/**
 * Adds or removes one project id from a widget's own `projectId` filter (the
 * editor's project picker for month-on-month "group by project" mode).
 * Refuses to add past `max` (a no-op, so a click at the cap does not silently
 * bump someone else's project out) and drops the filter entry entirely once
 * the last project is removed, rather than leaving an empty `in: []`
 * (`resolveWidgetFilters` in dashboardRequestKeys.ts already treats an empty
 * value list as "no narrowing", which is not what an empty picker means
 * here).
 */
export function toggleProjectIdFilter(filters: PnlWidgetFilter[], projectId: string, max: number): PnlWidgetFilter[] {
  const current = projectIdsFromFilters(filters);
  const withoutProjectFilter = filters.filter((f) => f.field !== "projectId" && f.field !== "project_id");

  if (current.includes(projectId)) {
    const next = current.filter((id) => id !== projectId);
    return next.length === 0 ? withoutProjectFilter : [...withoutProjectFilter, { field: "projectId", op: "in", value: next }];
  }
  if (current.length >= max) return filters;
  return [...withoutProjectFilter, { field: "projectId", op: "in", value: [...current, projectId] }];
}
